import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './PaymentResult.css';

function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  const merchantOid = searchParams.get('oid');
  const MAX_RETRIES = 5;
  const RETRY_DELAY = 2000;

  // Dil algılama
  useEffect(() => {
    const savedLang = localStorage.getItem('i18nextLng') || localStorage.getItem('language');
    if (savedLang && ['tr', 'en'].includes(savedLang)) {
      i18n.changeLanguage(savedLang);
    } else {
      const browserLang = navigator.language?.startsWith('tr') ? 'tr' : 'en';
      i18n.changeLanguage(browserLang);
    }
  }, [i18n]);

  const fetchPaymentStatus = useCallback(async (attempt = 0) => {
    try {
      const response = await axios.get(`/payment/status/${merchantOid}`);
      const paymentData = response.data.payment;

      // Callback henüz gelmemiş olabilir - status hala 'pending' ise retry
      if (paymentData.status === 'pending' && attempt < MAX_RETRIES) {
        setRetryCount(attempt + 1);
        setTimeout(() => fetchPaymentStatus(attempt + 1), RETRY_DELAY);
        return;
      }

      setPayment(paymentData);
      setLoading(false);
    } catch (err) {
      console.error('Payment status error:', err);

      // 404 - kayıt henüz oluşmamış olabilir, retry
      if (err.response?.status === 404 && attempt < MAX_RETRIES) {
        setRetryCount(attempt + 1);
        setTimeout(() => fetchPaymentStatus(attempt + 1), RETRY_DELAY);
        return;
      }

      setError(t('payment.notFound', 'Payment information not found'));
      setLoading(false);
    }
  }, [merchantOid, t]);

  useEffect(() => {
    if (merchantOid) {
      fetchPaymentStatus(0);
    } else {
      setError(t('payment.invalidPayment', 'Invalid payment'));
      setLoading(false);
    }
  }, [merchantOid, fetchPaymentStatus, t]);

  if (loading) {
    return (
      <div className="payment-result-page">
        <div className="payment-result-container">
          <div className="payment-result-card">
            <div className="spinner"></div>
            <p>{t('payment.verifying', 'Verifying payment...')}</p>
            {retryCount > 0 && (
              <p className="retry-info">
                {t('payment.waitingConfirmation', 'Waiting for payment confirmation...')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-result-page">
        <div className="payment-result-container">
          <div className="payment-result-card">
            <div className="icon-circle error">
              <span className="icon">⚠️</span>
            </div>
            <h1>{t('common.error', 'Error')}</h1>
            <p className="error-message">{error}</p>
            <button className="btn-primary" onClick={() => navigate('/dashboard')}>
              {t('payment.backToDashboard', 'Back to Dashboard')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-result-page">
      <div className="payment-result-container">
        <div className="payment-result-card success">
          <div className="icon-circle success">
            <svg width="80" height="80" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="38" fill="none" stroke="#10b981" strokeWidth="4" />
              <path d="M25 40 L35 50 L55 30" fill="none" stroke="#10b981" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <h1>{t('payment.successTitle', 'Payment Successful!')}</h1>
          <p className="success-message">
            {t('payment.successMessage', 'Your payment has been completed successfully. Your domain monitoring service has been activated.')}
          </p>

          {payment && (
            <div className="payment-details">
              {payment.domain_url && (
                <div className="detail-row">
                  <span className="detail-label">{t('domain.url', 'Domain')}:</span>
                  <span className="detail-value">{payment.domain_url}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">{t('payment.amount', 'Amount')}:</span>
                <span className="detail-value">
                  {payment.currency === 'USD' ? '$' : '₺'}{parseFloat(payment.amount).toFixed(2)}
                </span>
              </div>
              {payment.months && (
                <div className="detail-row">
                  <span className="detail-label">{t('payment.duration', 'Duration')}:</span>
                  <span className="detail-value">
                    {payment.months} {t('payment.months', 'months')}
                  </span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">{t('payment.status', 'Status')}:</span>
                <span className="detail-value">
                  <span className="badge success">{t('payment.completed', 'Completed')}</span>
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('payment.date', 'Date')}:</span>
                <span className="detail-value">
                  {new Date(payment.created_at).toLocaleString(i18n.language === 'tr' ? 'tr-TR' : 'en-US')}
                </span>
              </div>
            </div>
          )}

          <div className="action-buttons">
            <button className="btn-primary" onClick={() => navigate('/dashboard')}>
              {t('payment.backToDashboard', 'Back to Dashboard')}
            </button>
            <button className="btn-secondary" onClick={() => navigate('/domains')}>
              {t('nav.myDomains', 'My Domains')}
            </button>
          </div>

          <div className="info-box">
            <p>
              <strong>📧 {t('payment.emailSentTitle', 'Email Sent')}</strong><br />
              {t('payment.emailSentMessage', 'Payment details have been sent to your email address.')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccess;
